import React, { useState } from 'react';
import { Download, Shield, CheckCircle, AlertCircle, Monitor, Settings, Play } from 'lucide-react';

const Installation = () => {
  const [activeStep, setActiveStep] = useState(null);

  const steps = [
    {
      number: 1,
      title: 'Download Twe',
      description: 'Get the latest version for Windows',
      details: [
        'Click the "Download for Windows" button',
        'Choose your Windows version (Windows 10/11)',
        'File size: ~150MB',
        'Version: v3.0 (Latest)'
      ],
      icon: Download,
      color: 'orange'
    },
    {
      number: 2,
      title: 'Handle SmartScreen Warning',
      description: 'Windows may show a security warning',
      details: [
        'When you see "Windows protected your PC" message, click "More info"',
        'Click "Run anyway" to proceed with installation',
        'This is normal for new software that hasn\'t reached Microsoft\'s download threshold',
        'Twe is safe - built with Tauri/Rust and open source'
      ],
      icon: Shield,
      color: 'red'
    },
    {
      number: 3,
      title: 'Run the Installer',
      description: 'Follow the installation wizard',
      details: [
        'Double-click the downloaded .exe file',
        'Choose installation directory (default: C:\\Program Files\\Twe)',
        'Accept the license agreement',
        'Click "Install" and wait for completion (~2 minutes)'
      ],
      icon: Settings,
      color: 'orange'
    },
    {
      number: 4,
      title: 'First Launch & Setup',
      description: 'Configure Twe for your workflow',
      details: [
        'Launch Twe from desktop shortcut or Start menu',
        'Grant microphone permissions when prompted',
        'Enter your OpenAI API key (or skip for now)',
        'Complete the quick setup wizard (~1 minute)'
      ],
      icon: Play,
      color: 'green'
    }
  ];

  const systemRequirements = [
    { label: 'Operating System', value: 'Windows 10/11 (64-bit)' },
    { label: 'RAM', value: '4GB minimum, 8GB recommended' },
    { label: 'Storage', value: '1GB free space (estimated)' },
    { label: 'Browser', value: 'Chrome, Edge, or Firefox' },
    { label: 'Microphone', value: 'Required for voice control' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full mb-6">
            <Download className="w-10 h-10 text-orange-500" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Installation Guide
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Get Twe up and running in 5 minutes
          </p>
        </div>

        {/* Download CTA */}
        <div className="mb-16 bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-2xl p-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0">
              <h2 className="text-2xl font-bold text-white mb-2">
                Ready to install?
              </h2>
              <p className="text-gray-300">
                Download the latest version for Windows (v3.0)
              </p>
            </div>
            <button className="px-8 py-4 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-lg hover:shadow-xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 whitespace-nowrap flex items-center space-x-2">
              <Download className="w-5 h-5" />
              <span>Download Twe v3.0</span>
            </button>
          </div>
        </div>

        {/* Installation Steps */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Installation Steps</h2>
          <div className="space-y-6">
            {steps.map((step) => {
              const Icon = step.icon;
              const isActive = activeStep === step.number;
              
              return (
                <div
                  key={step.number}
                  className={`bg-zinc-900/50 border rounded-xl overflow-hidden transition-all duration-300 ${
                    isActive ? 'border-orange-500/50' : 'border-white/5'
                  }`}
                >
                  {/* Step Header */}
                  <button
                    onClick={() => setActiveStep(isActive ? null : step.number)}
                    className="w-full p-6 flex items-start space-x-4 hover:bg-white/5 transition-colors duration-200"
                  >
                    <div className={`w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br ${
                      step.color === 'orange' ? 'from-orange-500/20 to-red-500/20' :
                      step.color === 'red' ? 'from-red-500/20 to-orange-500/20' :
                      'from-green-500/20 to-emerald-500/20'
                    }`}>
                      <Icon className={`w-7 h-7 ${
                        step.color === 'orange' ? 'text-orange-500' :
                        step.color === 'red' ? 'text-red-500' :
                        'text-green-500'
                      }`} />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-sm font-semibold text-orange-500">Step {step.number}</span>
                        <h3 className="text-xl font-semibold text-white">{step.title}</h3>
                      </div>
                      <p className="text-gray-400">{step.description}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <svg
                        className={`w-6 h-6 text-gray-400 transition-transform duration-200 ${
                          isActive ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Step Details */}
                  {isActive && (
                    <div className="px-6 pb-6 border-t border-white/5">
                      <ul className="mt-6 space-y-3">
                        {step.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start space-x-3">
                            <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-300">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* System Requirements */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">System Requirements</h2>
          <div className="bg-zinc-900/50 border border-white/5 rounded-xl p-8">
            <div className="grid md:grid-cols-2 gap-6">
              {systemRequirements.map((req, idx) => (
                <div key={idx} className="flex items-start space-x-4">
                  <Monitor className="w-6 h-6 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-white font-semibold mb-1">{req.label}</h3>
                    <p className="text-gray-400">{req.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Troubleshooting */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-white mb-8">Common Issues</h2>
          <div className="space-y-4">
            {[
              {
                question: 'Installation is blocked by Windows Defender',
                answer: 'This is normal for new software. Click "More info" and then "Run anyway". Twe is safe and open source - you can verify the code on GitHub.'
              },
              {
                question: 'Microphone permissions not working',
                answer: 'Go to Windows Settings > Privacy > Microphone and enable permissions for Twe. Restart the app after granting permissions.'
              },
              {
                question: 'Voice commands not responding',
                answer: 'Ensure your OpenAI API key is correctly entered. Check your internet connection and microphone settings.'
              },
              {
                question: 'Browser automation not working',
                answer: 'Make sure you have Chrome, Edge, or Firefox installed. Twe needs browser drivers which are automatically downloaded on first use.'
              }
            ].map((item, idx) => (
              <div
                key={idx}
                className="bg-zinc-900/50 border border-white/5 rounded-xl p-6 hover:border-orange-500/30 transition-all duration-300"
              >
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{item.question}</h3>
                    <p className="text-gray-400 leading-relaxed">{item.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-gradient-to-br from-zinc-900 to-black border border-orange-500/30 rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Installation Complete?
          </h2>
          <p className="text-xl text-gray-400 mb-8">
            Start exploring Twe's powerful automation features
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/docs"
              className="px-6 py-3 bg-zinc-800 text-white font-medium rounded-lg hover:bg-zinc-700 transition-colors duration-200"
            >
              View Documentation
            </a>
            <a
              href="/recipes"
              className="px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-medium rounded-lg hover:shadow-lg hover:shadow-orange-500/50 transition-all duration-300"
            >
              Browse Automation Recipes
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Installation;